import { useRef } from "react";
import type { FC } from "react";
import { Names } from "./sections/Names";
import { Place } from "./sections/Place";
import { Date as EventDate } from "./sections/Date";
import { Program } from "./sections/Program";
import { Dresscode } from "./sections/Dresscode";
import { Wishes } from "./sections/Wishes";
import { GuestForm } from "./sections/GuestForm";
import { Timer } from "./sections/Timer";
import { Signature } from "./sections/Signature";
import { MadeBy } from "./sections/MadeBy";
import { MD_SCREEN_WIDTH, MIN_SCREEN_WIDTH } from "../../../constants";
import background from "../../../assets/images/templates/nezhnost/background.avif";
import { defaultTemplateImages } from "../defaultTemplateImages";
import clsx from "clsx";
import type { TemplateProps } from "../../../types";
import { usePreviewScrollPosition } from "../../../hooks/usePreviewScrollPosition";

export const NezhnostInvitation: FC<TemplateProps> = ({
  firstPartnerName,
  secondPartnerName,
  coupleImage,
  eventDate,
  place,
  colors,
  wishes,
  questions,
  answers,
  planItems,
  width,
  height,
  isMobile,
  isPreview,
  guestFormDisabled,
  block,
  id,
}) => {
  const scaleFactor = isPreview
    ? width / (isMobile ? MIN_SCREEN_WIDTH : MD_SCREEN_WIDTH)
    : 1;
  const scale = (value: number) => `${value * scaleFactor}px`;

  const parentRef = useRef<HTMLDivElement>(null);
  const namesRef = useRef<HTMLDivElement>(null);
  const dateRef = useRef<HTMLDivElement>(null);
  const placeRef = useRef<HTMLDivElement>(null);
  const programRef = useRef<HTMLDivElement>(null);
  const dresscodeRef = useRef<HTMLDivElement>(null);
  const wishesRef = useRef<HTMLDivElement>(null);
  const guestFormRef = useRef<HTMLDivElement>(null);

  usePreviewScrollPosition({
    gap: isMobile ? 120 * scaleFactor : 140 * scaleFactor,
    paddingTop: isMobile ? 34 * scaleFactor : 40 * scaleFactor,
    currentBlock: block,
    colors: colors,
    wishes: wishes,
    wishesRef: wishesRef,
    programRef: programRef,
    parentRef: parentRef,
    placeRef: placeRef,
    guestFormRef: guestFormRef,
    dresscodeRef: dresscodeRef,
    dateRef: dateRef,
    namesRef: namesRef,
  });

  return (
    <div
      className={clsx(
        "relative bg-white",
        isPreview &&
          "absolute h-full w-full overflow-hidden border-[3px] border-black",
      )}
      style={{
        borderRadius: scale(40),
        maxWidth: `${width}px`,
        maxHeight: height + "px",
      }}
    >
      <img
        className={clsx(
          "pointer-events-none inset-0 object-cover opacity-15",
          isPreview
            ? "absolute z-0 h-full w-full"
            : "fixed -z-50 h-screen w-full overflow-x-hidden",
        )}
        alt={""}
        src={background}
      />
      <div
        ref={parentRef}
        className={clsx(
          "relative z-10 flex h-full w-full flex-col items-center",
          isPreview && "scrollbar-hide overflow-y-auto overflow-x-hidden",
        )}
        style={{
          gap: isMobile ? scale(120) : scale(140),
          paddingInline: scale(15),
          paddingTop: isMobile ? scale(34) : scale(40),
        }}
      >
        <div ref={namesRef}>
          <Names
            firstPartnerName={firstPartnerName}
            secondPartnerName={secondPartnerName}
            coupleImage={
              coupleImage
                ? coupleImage
                : defaultTemplateImages.nezhnostCoupleImage
            }
            eventDate={eventDate}
            isMobile={isMobile}
            scale={scale}
          />
        </div>
        <div ref={dateRef}>
          <EventDate eventDate={eventDate} isMobile={isMobile} scale={scale} />
        </div>
        <div ref={placeRef}>
          <Place
            placeImage={
              place.placeImage
                ? place.placeImage
                : defaultTemplateImages.nezhnostPlaceImage
            }
            address={place.address}
            link={place.link}
            isMobile={isMobile}
            scale={scale}
          />
        </div>
        <div ref={programRef}>
          <Program planItems={planItems} isMobile={isMobile} scale={scale} />
        </div>
        {colors && (
          <div ref={dresscodeRef}>
            <Dresscode colors={colors} isMobile={isMobile} scale={scale} />
          </div>
        )}
        {wishes && (
          <div ref={wishesRef} className={"w-full"}>
            <Wishes wishes={wishes} isMobile={isMobile} scale={scale} />
          </div>
        )}
        <div ref={guestFormRef}>
          <GuestForm
            questions={questions}
            answers={answers}
            eventDate={eventDate}
            guestFormDisabled={guestFormDisabled}
            isMobile={isMobile}
            scale={scale}
            id={id ? id : -1}
          />
        </div>
        <Timer eventDate={eventDate} isMobile={isMobile} scale={scale} />
        <Signature isMobile={isMobile} scale={scale} />
        <MadeBy scale={scale} isMobile={isMobile} />
      </div>
    </div>
  );
};
